import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const isStaffLogin = location.pathname === '/login/staff';
  const { login, register } = useAuth();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '', role: isStaffLogin ? 'agent' : 'player' });
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(registerData.email, registerData.password, registerData.name, registerData.role);
      
      if (result.pending_approval) {
        toast.success('¡Solicitud enviada! Tu cuenta está pendiente de aprobación.', { duration: 8000 });
        setRegisterData({ email: '', password: '', name: '', role: isStaffLogin ? 'agent' : 'player' });
        return;
      }
      
      toast.success('¡Registro exitoso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en el registro');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('¡Bienvenido!');
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail, { duration: 6000 });
      } else {
        toast.error('Credenciales inválidas');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const LOGO_URL = 'https://customer-assets.emergentagent.com/job_player-connect-ikons/artifacts/bp71r7p3_IMG_9412.png';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col lg:flex-row">
      {/* Left side - Branding */}
      <div className="lg:w-1/2 bg-[#4F4F4F] flex flex-col justify-between p-8 lg:p-16 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />
        
        <div className="relative z-10">
          <img src={LOGO_URL} alt="Ikons" className="h-10 lg:h-12" />
        </div>
        
        <div className="relative z-10 py-8 lg:py-0">
          <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {isStaffLogin ? (
              <>Portal de<br /><span className="text-[#D4AF37]">Agentes</span></>
            ) : (
              <>Tu carrera<br />no se deja<br /><span className="text-[#D4AF37]">al azar.</span></>
            )}
          </h1>
          <p className="text-white/80 text-lg lg:text-xl max-w-md">
            {isStaffLogin 
              ? 'Acceso exclusivo para el equipo de representación.' 
              : 'Se construye con decisiones, seguimiento y compromiso.'}
          </p>
        </div>
        
        <div className="relative z-10 hidden lg:block">
          <p className="text-white/50 text-sm">© {new Date().getFullYear()} Ikons by Solocracks</p>
        </div>
      </div>

      {/* Right side - Forms */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <div className="mb-8">
            {/* Mobile logo */}
            <div className="lg:hidden mb-6 flex justify-center">
              <img src={LOGO_URL} alt="Ikons" className="h-10" />
            </div>
            <h2 className="text-2xl font-bold text-[#4F4F4F] mb-2">
              {isStaffLogin ? 'Acceso Staff' : 'Bienvenido'}
            </h2>
            <p className="text-[#808080]">
              {isStaffLogin ? 'Portal exclusivo para agentes' : 'Accede a tu cuenta o crea una nueva'}
            </p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100 p-1 rounded-lg">
              <TabsTrigger 
                value="login" 
                data-testid="login-tab"
                className="rounded-md data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white text-[#808080]"
              >
                Iniciar Sesión
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                data-testid="register-tab"
                className="rounded-md data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white text-[#808080]"
              >
                {isStaffLogin ? 'Solicitar' : 'Registrarse'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-[#4F4F4F]">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="tu@email.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="mt-1.5 h-12 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-[#4F4F4F]">Contraseña</Label>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="mt-1.5 h-12 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
                <Button 
                  type="submit" 
                  data-testid="login-submit-button"
                  className="w-full h-12 bg-[#D4AF37] hover:bg-[#A67C00] text-white font-bold rounded-full transition-all active:scale-[0.98]" 
                  disabled={loading}
                >
                  {loading ? 'Cargando...' : 'Iniciar Sesión'}
                </Button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-[#808080]">O continúa con</span>
                  </div>
                </div>

                <Button
                  type="button"
                  data-testid="google-login-button"
                  variant="outline"
                  className="w-full mt-4 h-12 bg-white border-gray-200 text-[#4F4F4F] hover:bg-gray-50 rounded-full"
                  onClick={handleGoogleLogin}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuar con Google
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name" className="text-[#4F4F4F]">Nombre Completo</Label>
                  <Input
                    id="register-name"
                    data-testid="register-name-input"
                    type="text"
                    placeholder="Juan Pérez"
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    required
                    className="mt-1.5 h-12 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-[#4F4F4F]">Email</Label>
                  <Input
                    id="register-email"
                    data-testid="register-email-input"
                    type="email"
                    placeholder="tu@email.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="mt-1.5 h-12 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-[#4F4F4F]">Contraseña</Label>
                  <Input
                    id="register-password"
                    data-testid="register-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    className="mt-1.5 h-12 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  />
                </div>
                {!isStaffLogin && (
                  <div>
                    <Label htmlFor="register-role" className="text-[#4F4F4F]">Rol</Label>
                    <Select
                      value={registerData.role}
                      onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                    >
                      <SelectTrigger 
                        data-testid="register-role-select" 
                        className="mt-1.5 h-12 bg-white border-gray-200 text-[#4F4F4F]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="player">Jugador</SelectItem>
                        <SelectItem value="agent">Agente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button 
                  type="submit" 
                  data-testid="register-submit-button"
                  className="w-full h-12 bg-[#D4AF37] hover:bg-[#A67C00] text-white font-bold rounded-full transition-all active:scale-[0.98]" 
                  disabled={loading}
                >
                  {loading ? 'Cargando...' : (isStaffLogin ? 'Solicitar Acceso' : 'Crear Cuenta')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <div className="mt-8 text-center">
            {isStaffLogin ? (
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-[#808080] hover:text-[#D4AF37] transition-colors flex items-center justify-center gap-2 mx-auto"
                data-testid="player-login-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al acceso de jugadores
              </button>
            ) : (
              <button
                onClick={() => navigate('/login/staff')}
                className="text-sm text-[#808080] hover:text-[#4F4F4F] transition-colors"
                data-testid="staff-login-link"
              >
                Acceso Staff
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
